import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Cookie, Settings, Check, X } from 'lucide-react';
import CookieConsentManager, { CookieConsentChoice } from '@/lib/cookieConsentManager';

interface CookiePreferences {
  necessary: boolean; // Always true, cannot be disabled
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    if (!CookieConsentManager.hasMadeChoice()) {
      // Show banner after a brief delay to avoid showing immediately on page load
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    setPreferences({
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    });
    CookieConsentManager.acceptCookies();
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    setPreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    });
    CookieConsentManager.rejectCookies();
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    // For simplicity, if any non-necessary cookie is accepted, consider it as "accepted"
    const hasAcceptedAny = preferences.analytics || preferences.marketing || preferences.functional;

    if (hasAcceptedAny) {
      CookieConsentManager.acceptCookies();
    } else {
      CookieConsentManager.rejectCookies();
    }

    setShowPreferencesModal(false);
    setIsVisible(false);
  };

  const handleManagePreferences = () => {
    setShowPreferencesModal(true);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Cookie Consent Banner */}
      <div className="fixed bottom-4 right-4 z-50 bg-background border border-border shadow-lg p-4 rounded-lg max-w-sm">
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <Cookie className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-medium text-sm mb-1">Cookie Preferences</h3>
              <p className="text-xs text-muted-foreground">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
                By clicking "Accept All", you consent to our use of cookies.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleManagePreferences}
                className="h-8 text-xs flex-1"
              >
                <Settings className="h-3 w-3 mr-1" />
                Manage
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                className="h-8 text-xs flex-1"
              >
                Reject All
              </Button>
            </div>
            <Button
              size="sm"
              onClick={handleAcceptAll}
              className="h-8 text-xs bg-primary hover:bg-primary/90"
            >
              Accept All
            </Button>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      <Dialog open={showPreferencesModal} onOpenChange={setShowPreferencesModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Cookie className="h-4 w-4" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription className="text-xs">
              Choose which cookies to allow.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1">
            {/* Necessary Cookies */}
            <Card className="card-no-animations">
              <CardContent className="p-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Necessary</Label>
                    <p className="text-xs text-muted-foreground">Required for website functionality</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-green-600">Always</span>
                    <Check className="h-3 w-3 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Analytics Cookies */}
            <Card className="card-no-animations">
              <CardContent className="p-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Analytics</Label>
                    <p className="text-xs text-muted-foreground">Help understand visitor behavior</p>
                  </div>
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={(checked) =>
                      setPreferences(prev => ({ ...prev, analytics: checked }))
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Marketing Cookies */}
            <Card className="card-no-animations">
              <CardContent className="p-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Marketing</Label>
                    <p className="text-xs text-muted-foreground">Personalized ads and campaigns</p>
                  </div>
                  <Switch
                    checked={preferences.marketing}
                      onCheckedChange={(checked) =>
                        setPreferences(prev => ({ ...prev, marketing: checked }))
                      }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Functional Cookies */}
            <Card className="card-no-animations">
              <CardContent className="p-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Functional</Label>
                    <p className="text-xs text-muted-foreground">Enhanced features and personalization</p>
                  </div>
                  <Switch
                    checked={preferences.functional}
                    onCheckedChange={(checked) =>
                      setPreferences(prev => ({ ...prev, functional: checked }))
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRejectAll}
              className="w-full sm:w-auto h-7 text-xs"
            >
              Reject All
            </Button>
            <Button
              size="sm"
              onClick={handleSavePreferences}
              className="w-full sm:w-auto h-7 text-xs"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
